import type { LineStringGeometry, PointGeometry, PolygonGeometry, Role } from '@trafiku/contracts';

export interface Station {
  _id: string;
  name: string;
  description?: string;
  geometry: PointGeometry;
  lines: string[];
  unique_id: string;
}
export interface Line {
  _id: string;
  name: string;
  description?: string;
  geometry: LineStringGeometry;
  ordered_stations: Array<{ _id: string; name: string; geometry?: PointGeometry }>;
  color?: string;
  bus_sign?: string;
}
export interface Zone {
  _id: string;
  name: string;
  description?: string;
  bus_sign: string;
  color: string;
  sort: number;
  is_active: boolean;
  geometry_going: PolygonGeometry;
  geometry_return: PolygonGeometry;
  on_line_going?: string | null;
  on_line_return?: string | null;
}
export interface Bus {
  _id: string;
  name?: string;
  description?: string;
  number?: string;
  arvento_id?: string;
  color: string;
  geometry?: PointGeometry;
  heading?: number;
  speed?: number;
  avg_15min?: number;
  is_active: boolean;
  is_visible: boolean;
  address?: string;
  zone?: { _id: string; name: string; bus_sign: string; color: string } | null;
  line?: { _id: string; name: string } | null;
  /** 'new': reported by Arvento for the first time, not sorted yet; 'other': not a bus. */
  vehicle_type?: 'bus' | 'new' | 'other';
  vehicle_note?: string;
}
export interface Schedule {
  _id: string;
  line: { _id: string; name: string };
  station: { _id: string; name: string };
  week: string[];
  weekend: string[];
}
export interface OutOfServiceArea {
  _id: string;
  name: string;
  description?: string;
  geometry: PolygonGeometry;
}
export interface InterestPoint {
  _id: string;
  title: string;
  category: string;
  latitude: number;
  longitude: number;
  icon: string;
  photo: string;
  is_active: boolean;
  link: string;
  description: string;
}
export interface News {
  _id: string;
  title: string;
  short_description: string;
  description: string;
  photo?: string;
  created_date: string;
  updated_date?: string | null;
}
export interface Report {
  _id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  description: string;
  created_at: string;
  is_active: boolean;
  reply?: { description: string; reply_user_email: string; created_at: string; is_read: boolean };
  user?: { fullname?: string; email?: string; phone_no?: string; sign_in_provider?: string } | null;
}
export interface Notification {
  _id: string;
  title: string;
  body: string;
  is_active: boolean;
  created_at: string;
  created_by?: { email: string } | null;
}
export interface Campaign {
  _id: string;
  name: string;
  description?: string;
  contact: string;
  emails: string[];
  notify: boolean;
  is_active: boolean;
  created_date: string;
  advertisements?: Advertisement[];
  pinadvertisements?: PinAdvertisement[];
}
export interface Advertisement {
  _id: string;
  title: string;
  description?: string;
  photo: string;
  icon: string;
  text: string;
  text_color: string;
  link: string;
  ios_link: string;
  android_link: string;
  is_active: boolean;
  priority: number;
  views_count: number;
  clicks_count: number;
  campaign_id: string | { _id: string; name: string; is_active: boolean };
}
export interface PinAdvertisement {
  _id: string;
  name: string;
  description?: string;
  geometry: PointGeometry;
  web_view: string;
  icon?: string;
  pin_icon?: string;
  background_image?: string;
  priority: number;
  is_active: boolean;
  views_count: number;
  clicks_count: number;
  campaign_id: string | { _id: string; name: string; is_active: boolean };
}
export interface AdminUser {
  _id: string;
  email: string;
  role: Role | null;
  is_active: boolean;
  created_at?: string;
}
export interface AuditEntry {
  _id: string;
  at: string;
  user_email?: string;
  action: string;
  entity: string;
  entity_id?: string;
  summary?: string;
  changes?: unknown;
}

export const refId = (v: string | { _id: string } | null | undefined) =>
  typeof v === 'string' ? v : (v?._id ?? '');
